import { useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import type { Database } from "@/lib/database.types";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { fetchPracticeHistory, type PracticeSessionRow } from "@/lib/api/practice";

const MAX_HISTORY_DISPLAY = 6;

type SubjectRow = Database["public"]["Tables"]["subjects"]["Row"];
type UnitRow = Database["public"]["Tables"]["units"]["Row"];
type TopicRow = Database["public"]["Tables"]["topics"]["Row"];

type UnitWithTopics = UnitRow & { topics: TopicRow[] };

type SubjectOutlineResult = {
  subject: SubjectRow;
  units: UnitWithTopics[];
};

export default function SubjectStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackSubjectSelected } = useAnalytics();

  // outline query
  const outlineQuery = useQuery<SubjectOutlineResult>({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  // subjectId derived from outlineQuery.data, used in other queries
  const subjectId = outlineQuery.data?.subject.id;

  // Track subject selection once when data loads
  const trackedRef = useRef<string | null>(null);
  const subjectData = outlineQuery.data?.subject ?? null;
  
  useEffect(() => {
    if (!slug || !subjectData || trackedRef.current === slug) return;
    
    trackSubjectSelected({
      subjectSlug: slug,
      subjectName: subjectData.name ?? slug,
    });
    
    trackedRef.current = slug;
  }, [slug, subjectData, trackSubjectSelected]);

  // practice history query
  const practiceHistoryQuery = useQuery<PracticeSessionRow[]>({
    queryKey: ["practice-history", user?.id, subjectId],
    queryFn: () => fetchPracticeHistory(user!.id, subjectId!, 20),
    enabled: Boolean(user?.id && subjectId),
  });

  // Loading state
  if (outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Loading subject…</CardTitle>
              <CardDescription>Fetching subject outline.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">This usually takes just a moment.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Error or missing data state
  if (outlineQuery.isError || !outlineQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="bg-gradient-card border">
            <CardHeader>
              <CardTitle className="text-xl">Subject not found</CardTitle>
              <CardDescription>The subject you tried to open could not be located.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/subjects">Browse Subjects</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Safe to destructure now
  const { subject } = outlineQuery.data;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Badge variant="secondary" className="ml-1">
              Subject
            </Badge>
          </div>
        </div>

        <div className="mb-2">
          <h1 className="text-2xl sm:text-3xl font-bold">{subject.name ?? subject.slug}</h1>
          <p className="text-muted-foreground mt-1">Start practicing questions for this subject.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          <ToolCard
            title="Practice Questions"
            description="Build custom practice sets by unit and topic."
            cta="Start"
            href={`/practice/${slug}/setup`}
            live
          />
          <ToolCard
            title="Personalized Plans"
            description="Tailor-made tests based on your weak areas."
            cta="Start"
            href={`/subjects/${slug}/personalized`}
            live
          />
          <ToolCard
            title="Notes and Resources"
            description="Key concepts, formulas, and summaries."
            cta="View"
            href={`/subjects/${slug}/notes`}
            live
          />
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-3">Previous tests</h2>
          <PrevTests
            slug={slug!}
            loading={practiceHistoryQuery.isLoading}
            history={practiceHistoryQuery.data ?? []}
          />
        </div>
      </main>
    </div>
  );
}

type ToolCardProps = {
  title: string;
  description: string;
  cta: string;
  href?: string;
  onClick?: () => void;
  live?: boolean;
};

function ToolCard({ title, description, cta, href, onClick, live }: ToolCardProps) {
  return (
    <Card className={`border ${live ? "bg-background" : "opacity-80"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          {title} {live && <Badge className="ml-2" variant="secondary">Live</Badge>}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {live ? (
          href ? (
            <Button asChild>
              <Link to={href}>{cta}</Link>
            </Button>
          ) : onClick ? (
            <Button onClick={onClick}>{cta}</Button>
          ) : (
            <Button disabled>{cta}</Button>
          )
        ) : (
          <Button disabled>{cta}</Button>
        )}
      </CardContent>
    </Card>
  );
}

type PrevTestsProps = {
  slug: string;
  loading: boolean;
  history: PracticeSessionRow[];
};

function PrevTests({ slug, loading, history }: PrevTestsProps) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading your recent practice sessions…</p>;
  }

  if (!history.length) {
    return <p className="text-sm text-muted-foreground">No tests yet. Run a practice set to see history here.</p>;
  }

  const chronological = history
    .slice()
    .sort((a, b) => {
      const aTime = a.completed_at ?? a.started_at ?? "";
      const bTime = b.completed_at ?? b.started_at ?? "";
      return aTime.localeCompare(bTime);
    });

  const numberById = new Map<string, number>(chronological.map((row, index) => [row.id, index + 1]));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {history.slice(0, MAX_HISTORY_DISPLAY).map((session) => {
        const rawScore = session.score;
        const pct = rawScore == null ? null : Math.round(Number(rawScore));
        const when = session.completed_at || session.started_at;
        const color =
          pct == null
            ? "bg-muted text-muted-foreground"
            : pct >= 80
            ? "bg-emerald-100 text-emerald-700"
            : pct >= 60
            ? "bg-amber-100 text-amber-700"
            : "bg-rose-100 text-rose-700";
        const testNo = numberById.get(session.id) ?? "?";

        return (
          <Card key={session.id} className="border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Test {testNo}</CardTitle>
              <CardDescription>{when ? new Date(when).toLocaleString() : "In progress"}</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <span className={`text-sm px-2 py-0.5 rounded ${color}`}>{pct != null ? `${pct}%` : "—"}</span>
              <div className="flex gap-2">
                <Button variant="outline" asChild size="sm">
                  <Link to={`/practice/${slug}/summary?id=${session.id}`}>Summary</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to={`/practice/${slug}/setup`}>New set</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

